import React, { useState, useEffect, useRef } from 'react';
import LandingPageHeader from '../components/LandingPageHeader';
import LandingPageFooter from '../components/LandingPageFooter';
import ContactForm from '../components/ContactForm';
import PricingSection from '../components/PricingSection';
import TestimonialsSection from '../components/TestimonialsSection';
import { usePopup } from '../context/PopupContext';
import { CheckCircle, Shield, FileText, Award } from 'lucide-react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';

const LandingPage = () => {
  const { openPopup } = usePopup();
  const [counters, setCounters] = useState([0, 0, 0, 0]);
  const [hasStarted, setHasStarted] = useState(false);
  const statsRef = useRef(null);

  const handleChatClick = () => {
    // Open Chattrik chat widget
    if (typeof window.cd360Api !== 'undefined' && window.cd360Api) {
      try {
        window.cd360Api.liveChat.showWebWidget();
      } catch (e) {
        console.log('Chattrik widget error:', e);
        alert('Chat is loading. Please try again in a moment or contact us at: info@trademarkvector.com');
      }
    } else {
      console.log('Chattrik widget not loaded yet');
      alert('Chat widget is loading. Please try again in a moment or contact us at: info@trademarkvector.com');
    }
  };

  const processSteps = [
    {
      Icon: FileText,
      title: 'Submit Your Details',
      description: 'Provide us with your brand name, logo, and basic business information through our secure online form.',
      color: 'from-blue-500 to-blue-600'
    },
    {
      Icon: Shield,
      title: 'We Handle the Filing',
      description: 'Our experts conduct a trademark search, prepare your application, and file it with the USPTO — accurately and efficiently.',
      color: 'from-purple-500 to-purple-600'
    },
    {
      Icon: Award,
      title: 'Get Official Protection',
      description: 'Once approved, your trademark is officially registered, giving you exclusive legal rights and nationwide brand protection.',
      color: 'from-green-500 to-green-600'
    }
  ];

  const stats = [
    { number: '98%', label: 'Approval Rate', target: 98 },
    { number: '24/7', label: 'Customer Support', target: 24 },
    { number: '10,000+', label: 'Trademarks Filed', target: 10000 },
    { number: '50+', label: 'States Covered', target: 50 }
  ];

  // Counter animation logic
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasStarted) {
          setHasStarted(true);
        }
      },
      { threshold: 0.3 }
    );

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }

    return () => observer.disconnect();
  }, [hasStarted]);

  useEffect(() => {
    if (!hasStarted) return;

    const duration = 2000;
    const steps = 60;
    const interval = duration / steps;

    const timers = stats.map((stat, index) => {
      let current = 0;
      const increment = stat.target / steps;

      return setInterval(() => {
        current += increment;
        if (current >= stat.target) {
          current = stat.target;
          clearInterval(timers[index]);
        }
        setCounters(prev => {
          const newCounters = [...prev];
          newCounters[index] = Math.floor(current);
          return newCounters;
        });
      }, interval);
    });

    return () => timers.forEach(timer => clearInterval(timer));
  }, [hasStarted]);

  const formatNumber = (num, originalStat) => {
    if (originalStat.includes('24/7')) return '24/7';
    const formatted = num.toLocaleString();
    if (originalStat.includes('+')) return formatted + '+';
    if (originalStat.includes('%')) return formatted + '%';
    return formatted;
  };

  const features = [
    'Trademark Search & Clearance',
    'USPTO Trademark Filing',
    'Office Action Responses',
    'Trademark Renewals'
  ];

  const benefits = [
    {
      Icon: Shield,
      title: '100% Money-Back Guarantee',
      description: 'If we can\'t file your trademark for any reason, you get a full refund. No questions asked.',
      color: 'from-blue-500 to-blue-600'
    },
    {
      Icon: CheckCircle,
      title: 'Free Comprehensive Search',
      description: 'We conduct a thorough trademark search to ensure your mark is available before filing.',
      color: 'from-green-500 to-green-600'
    },
    {
      Icon: Award,
      title: 'Expert Legal Support',
      description: 'USPTO-licensed attorneys review every application to maximize your chances of approval.',
      color: 'from-purple-500 to-purple-600'
    },
    {
      Icon: FileText,
      title: 'Same-Day Filing Available',
      description: 'Submit today, and we\'ll file your trademark application with the USPTO within 24 hours.',
      color: 'from-red-500 to-red-600'
    },
    {
      Icon: CheckCircle,
      title: 'Office Action Response',
      description: 'If the USPTO has questions, our attorneys handle all correspondence on your behalf.',
      color: 'from-yellow-500 to-yellow-600'
    },
    {
      Icon: Shield,
      title: 'Lifetime Support',
      description: 'We\'re here for you even after registration—renewals, monitoring, and ongoing consultation.',
      color: 'from-indigo-500 to-indigo-600'
    }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <LandingPageHeader />

      {/* Hero Section with Form */}
      <section className="py-20 bg-gradient-to-br from-[#C0392B] via-[#A93226] to-[#8B2E1F] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '40px 40px'}} />
        </div>
        
        <div className="container mx-auto px-4 max-w-[1320px] relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Form - Order 1 on mobile, 2 on desktop */}
            <div className="bg-white rounded-2xl shadow-2xl p-8 order-1 lg:order-2">
              <div className="bg-[#C0392B] text-white text-center py-2 px-4 rounded-md mb-4">
                <span className="font-bold">🔥 LIMITED TIME OFFER - FILE FOR JUST $45</span>
              </div>
              <h3 className="text-2xl font-bold text-[#2C3E50] mb-2">Get Your FREE Trademark Search!</h3>
              <p className="text-[#7F8C8D] mb-6">Fill out the form and our experts will contact you within 1 hour to discuss your trademark protection</p>
              <ContactForm isCompact={true} />
              <p className="text-xs text-gray-500 text-center mt-4">
                🔒 Your information is 100% secure and confidential
              </p>
            </div>

            {/* Content - Order 2 on mobile, 1 on desktop */}
            <div className="text-white order-2 lg:order-1">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                Protect Your Brand Before Someone Else Does!
              </h1>
              <p className="text-xl mb-4 text-white/90">
                Don't let competitors steal your brand name. Secure your exclusive rights with professional trademark registration starting at just $45.
              </p>
              <ul className="space-y-3 mb-8 text-lg">
                <li className="flex items-start">
                  <CheckCircle className="w-6 h-6 mr-3 flex-shrink-0 mt-1" />
                  <span><strong>98% Success Rate</strong> - Proven track record of approvals</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-6 h-6 mr-3 flex-shrink-0 mt-1" />
                  <span><strong>Same-Day Filing</strong> - Get your application submitted fast</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-6 h-6 mr-3 flex-shrink-0 mt-1" />
                  <span><strong>Expert Legal Support</strong> - USPTO-licensed attorneys on your side</span>
                </li>
              </ul>
              <div className="flex flex-wrap gap-4 mb-8">
                <button
                  onClick={openPopup}
                  className="bg-white text-[#C0392B] hover:bg-gray-100 px-8 py-4 rounded-md font-bold text-lg transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                  Start For Just $45
                </button>
                <button
                  onClick={handleChatClick}
                  className="bg-transparent text-white border-2 border-white hover:bg-white hover:text-[#C0392B] px-8 py-4 rounded-md font-bold text-lg transition-all duration-300"
                >
                  Talk To An Expert
                </button>
              </div>
              <p className="text-sm text-white/80">
                ⚡ Limited Time: File today and get FREE trademark monitoring for 6 months!
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section ref={statsRef} className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-[1320px]">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <h3 className="text-4xl md:text-5xl font-bold text-[#C0392B] mb-2">
                  {formatNumber(counters[index], stat.number)}
                </h3>
                <p className="text-[#7F8C8D] font-semibold">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Do Section */}
      <section className="py-20 bg-gradient-to-b from-white to-[#FDFEFE]">
        <div className="container mx-auto px-4 max-w-[1320px]">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Image - Order 1 on mobile, 2 on desktop */}
            <div className="relative order-1 lg:order-2">
              <img
                src="https://images.unsplash.com/photo-1642522029691-029b5a432954?w=800&q=80"
                alt="Professional Business Meeting"
                className="w-full h-auto rounded-2xl shadow-2xl"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#C0392B]/20 to-transparent rounded-2xl"></div>
            </div>
            
            {/* Content - Order 2 on mobile, 1 on desktop */}
            <div className="order-2 lg:order-1">
              <h5 className="text-[#C0392B] font-bold text-lg mb-4">Why Choose Us</h5>
              <h2 className="text-4xl md:text-5xl font-bold text-[#2C3E50] mb-6">
                The Smartest Way to Protect Your Business Assets
              </h2>
              <p className="text-[#7F8C8D] text-lg mb-6 leading-relaxed">
                Your brand is your most valuable asset. Without trademark protection, anyone can copy your name, logo, or slogan—damaging your reputation and stealing your customers. We've helped over 10,000 businesses secure their brands with hassle-free trademark registration.
              </p>
              <ul className="space-y-3 mb-8">
                {features.map((feature, index) => (
                  <li key={index} className="flex items-center">
                    <CheckCircle className="w-6 h-6 text-[#C0392B] mr-3 flex-shrink-0" />
                    <span className="text-[#2C3E50] font-medium">{feature}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={openPopup}
                className="bg-[#C0392B] hover:bg-[#A93226] text-white px-8 py-4 rounded-md font-bold text-lg transition-all duration-300 hover:shadow-xl"
              >
                Secure Your Brand Now - Only $45
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Process Steps Section */}
      <section className="py-20 bg-[#2C3E50]">
        <div className="container mx-auto px-4 max-w-[1320px]">
          <div className="text-center mb-16">
            <h5 className="text-[#C0392B] font-bold text-lg mb-4">Simple 3-Step Process</h5>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              From Application to Approval in Record Time
            </h2>
            <p className="text-gray-300 text-lg max-w-4xl mx-auto">
              We've streamlined the complex USPTO filing process into three simple steps. No legal jargon, no hidden fees, no surprises—just professional trademark protection in as little as 6-8 months.
            </p>
          </div>

          {/* Desktop View - Grid */}
          <div className="hidden md:grid md:grid-cols-3 gap-8">
            {processSteps.map((step, index) => {
              const Icon = step.Icon;
              return (
                <div 
                  key={index} 
                  className="group relative bg-white/5 backdrop-blur-sm rounded-2xl p-8 border-2 border-white/10 hover:border-[#C0392B] hover:bg-white/10 transition-all duration-500 hover:scale-105 hover:shadow-2xl cursor-pointer"
                >
                  {/* Step Number Badge */}
                  <div className="absolute -top-4 -right-4 w-12 h-12 bg-gradient-to-br from-[#C0392B] to-[#A93226] rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                    {index + 1}
                  </div>

                  {/* Animated Icon with Gradient */}
                  <div className={`w-20 h-20 mx-auto mb-6 bg-gradient-to-br ${step.color} rounded-2xl flex items-center justify-center group-hover:rotate-12 group-hover:scale-110 transition-all duration-500 shadow-lg`}>
                    <Icon className="w-10 h-10 text-white group-hover:scale-125 transition-transform duration-300" />
                  </div>

                  {/* Title with underline animation */}
                  <h3 className="text-2xl font-bold text-white mb-4 text-center relative">
                    {step.title}
                    <span className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-0 h-1 bg-[#C0392B] group-hover:w-full transition-all duration-500"></span>
                  </h3>

                  {/* Description */}
                  <p className="text-gray-300 leading-relaxed text-center group-hover:text-white transition-colors duration-300">
                    {step.description}
                  </p>

                  {/* Decorative corner */}
                  <div className="absolute bottom-4 right-4 w-8 h-8 border-r-2 border-b-2 border-[#C0392B]/30 group-hover:border-[#C0392B] transition-all duration-300"></div>
                </div>
              );
            })}
          </div>

          {/* Mobile & Tablet View - Slider */}
          <div className="md:hidden">
            <Swiper
              modules={[Pagination, Autoplay]}
              spaceBetween={20}
              slidesPerView={1}
              pagination={{ clickable: true }}
              autoplay={{ delay: 3000, disableOnInteraction: false }}
              className="pb-12"
            >
              {processSteps.map((step, index) => {
                const Icon = step.Icon;
                return (
                  <SwiperSlide key={index}>
                    <div className="px-4">
                      <div className="group relative bg-white/5 backdrop-blur-sm rounded-2xl p-8 border-2 border-white/10 hover:border-[#C0392B] hover:bg-white/10 transition-all duration-500 hover:shadow-2xl">
                        {/* Step Number Badge */}
                        <div className="absolute -top-4 -right-4 w-12 h-12 bg-gradient-to-br from-[#C0392B] to-[#A93226] rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg">
                          {index + 1}
                        </div>

                        {/* Animated Icon with Gradient */}
                        <div className={`w-20 h-20 mx-auto mb-6 bg-gradient-to-br ${step.color} rounded-2xl flex items-center justify-center transition-all duration-500 shadow-lg`}>
                          <Icon className="w-10 h-10 text-white" />
                        </div>

                        {/* Title */}
                        <h3 className="text-2xl font-bold text-white mb-4 text-center">
                          {step.title}
                        </h3>

                        {/* Description */}
                        <p className="text-gray-300 leading-relaxed text-center">
                          {step.description}
                        </p>
                      </div>
                    </div>
                  </SwiperSlide>
                );
              })}
            </Swiper>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-gradient-to-br from-[#C0392B] via-[#A93226] to-[#8B2E1F] relative overflow-hidden">
        <div className="absolute top-0 left-0 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '40px 40px'}} />
        </div>
        
        <div className="container mx-auto px-4 max-w-[1320px] relative z-10">
          <div className="text-center mb-16">
            <h5 className="text-white/80 font-bold text-lg mb-4">Our Pricing</h5>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Pick Your Trademark Package!
            </h2>
          </div>
          <PricingSection service="trademark-registration" />
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gradient-to-b from-white to-[#FDFEFE] relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{backgroundImage: 'radial-gradient(circle, #C0392B 1px, transparent 1px)', backgroundSize: '40px 40px'}} />
        </div>

        <div className="container mx-auto px-4 max-w-[1320px] relative z-10">
          <div className="text-center mb-16">
            <h5 className="text-[#C0392B] font-bold text-lg mb-4">Benefits</h5>
            <h2 className="text-4xl md:text-5xl font-bold text-[#2C3E50] mb-6">
              What You Get with Trademark Vector
            </h2>
            <p className="text-[#7F8C8D] text-lg max-w-3xl mx-auto">
              When you choose us for trademark registration, you're not just filing paperwork—you're gaining a trusted partner dedicated to protecting your brand.
            </p>
          </div>

          {/* Desktop View - Grid */}
          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.Icon;
              return (
                <div key={index} className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 p-8 border border-gray-100 hover:border-[#C0392B] hover:-translate-y-2">
                  <div className={`w-16 h-16 bg-gradient-to-br ${benefit.color} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300`}>
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-[#2C3E50] mb-3">{benefit.title}</h3>
                  <p className="text-[#7F8C8D] leading-relaxed">
                    {benefit.description}
                  </p>
                </div>
              );
            })}
          </div>

          {/* Mobile View - Slider */}
          <div className="md:hidden">
            <Swiper
              modules={[Pagination, Autoplay]}
              spaceBetween={20}
              slidesPerView={1}
              pagination={{ clickable: true }}
              autoplay={{ delay: 3000, disableOnInteraction: false }}
              className="pb-12"
            >
              {benefits.map((benefit, index) => {
                const Icon = benefit.Icon;
                return (
                  <SwiperSlide key={index}>
                    <div className="px-4">
                      <div className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 p-8 border border-gray-100 hover:border-[#C0392B]">
                        <div className={`w-16 h-16 bg-gradient-to-br ${benefit.color} rounded-2xl flex items-center justify-center mb-6 transition-all duration-300`}>
                          <Icon className="w-8 h-8 text-white" />
                        </div>
                        <h3 className="text-xl font-bold text-[#2C3E50] mb-3">{benefit.title}</h3>
                        <p className="text-[#7F8C8D] leading-relaxed">
                          {benefit.description}
                        </p>
                      </div>
                    </div>
                  </SwiperSlide>
                );
              })}
            </Swiper>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <TestimonialsSection />

      {/* FAQs Section */}
      <section className="py-20 bg-gradient-to-b from-white to-[#FDFEFE] relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{backgroundImage: 'radial-gradient(circle, #C0392B 1px, transparent 1px)', backgroundSize: '40px 40px'}} />
        </div>

        <div className="container mx-auto px-4 max-w-[1320px] relative z-10">
          <div className="text-center mb-16">
            <h5 className="text-[#C0392B] font-bold text-lg mb-4">FAQs</h5>
            <h2 className="text-4xl md:text-5xl font-bold text-[#2C3E50] mb-6">
              Frequently Asked Questions
            </h2>
            <p className="text-[#7F8C8D] text-lg max-w-3xl mx-auto">
              Get answers to common questions about trademark registration and our services.
            </p>
          </div>

          <div className="max-w-4xl mx-auto space-y-4">
            {/* FAQ 1 */}
            <details className="group bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-200">
              <summary className="flex items-center justify-between cursor-pointer p-6 font-bold text-lg text-[#2C3E50] list-none">
                <span>How long does the trademark registration process take?</span>
                <span className="ml-4 text-[#C0392B] text-2xl group-open:rotate-45 transition-transform duration-300">+</span>
              </summary>
              <div className="px-6 pb-6 text-[#7F8C8D] leading-relaxed border-t border-gray-100 pt-4">
                The entire process typically takes 6-12 months from filing to registration. We file your application with the USPTO within 24 hours of receiving your information, and you'll receive confirmation immediately. The USPTO review process usually takes 3-4 months for initial feedback.
              </div>
            </details>

            {/* FAQ 2 */}
            <details className="group bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-200">
              <summary className="flex items-center justify-between cursor-pointer p-6 font-bold text-lg text-[#2C3E50] list-none">
                <span>What's included in the $45 package?</span>
                <span className="ml-4 text-[#C0392B] text-2xl group-open:rotate-45 transition-transform duration-300">+</span>
              </summary>
              <div className="px-6 pb-6 text-[#7F8C8D] leading-relaxed border-t border-gray-100 pt-4">
                Our $45 Basic package includes a comprehensive trademark search, preparation and filing of your USPTO trademark application, and ongoing status monitoring. You'll also get unlimited phone and email support throughout the process. USPTO filing fees are separate and paid directly to the government.
              </div>
            </details>

            {/* FAQ 3 */}
            <details className="group bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-200">
              <summary className="flex items-center justify-between cursor-pointer p-6 font-bold text-lg text-[#2C3E50] list-none">
                <span>Do I really need to register my trademark?</span>
                <span className="ml-4 text-[#C0392B] text-2xl group-open:rotate-45 transition-transform duration-300">+</span>
              </summary>
              <div className="px-6 pb-6 text-[#7F8C8D] leading-relaxed border-t border-gray-100 pt-4">
                Yes! Without federal trademark registration, you have no legal protection. Anyone can use your brand name, logo, or slogan in the same industry. Registration gives you exclusive nationwide rights, legal presumption of ownership, and the ability to sue infringers in federal court. It's the best way to protect your brand investment.
              </div>
            </details>

            {/* FAQ 4 */}
            <details className="group bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-200">
              <summary className="flex items-center justify-between cursor-pointer p-6 font-bold text-lg text-[#2C3E50] list-none">
                <span>What if my trademark application is rejected?</span>
                <span className="ml-4 text-[#C0392B] text-2xl group-open:rotate-45 transition-transform duration-300">+</span>
              </summary>
              <div className="px-6 pb-6 text-[#7F8C8D] leading-relaxed border-t border-gray-100 pt-4">
                If the USPTO issues an office action (refusal), our attorneys will review it and prepare a comprehensive response at no extra charge (included in Standard and Premium packages). We have a 98% success rate in overcoming office actions. If we can't file your trademark for any reason, you get a 100% money-back guarantee.
              </div>
            </details>

            {/* FAQ 5 */}
            <details className="group bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-200">
              <summary className="flex items-center justify-between cursor-pointer p-6 font-bold text-lg text-[#2C3E50] list-none">
                <span>Can I trademark a name that's already in use?</span>
                <span className="ml-4 text-[#C0392B] text-2xl group-open:rotate-45 transition-transform duration-300">+</span>
              </summary>
              <div className="px-6 pb-6 text-[#7F8C8D] leading-relaxed border-t border-gray-100 pt-4">
                It depends. If someone else is already using an identical or similar mark in the same industry, you likely can't register it. However, the same name can be trademarked by different companies in different industries (e.g., Delta Airlines vs. Delta Faucets). Our comprehensive trademark search will identify any conflicts before you waste money filing.
              </div>
            </details>

            {/* FAQ 6 */}
            <details className="group bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-200">
              <summary className="flex items-center justify-between cursor-pointer p-6 font-bold text-lg text-[#2C3E50] list-none">
                <span>What information do I need to provide?</span>
                <span className="ml-4 text-[#C0392B] text-2xl group-open:rotate-45 transition-transform duration-300">+</span>
              </summary>
              <div className="px-6 pb-6 text-[#7F8C8D] leading-relaxed border-t border-gray-100 pt-4">
                You'll need to provide: (1) The exact name or logo you want to trademark, (2) A description of your goods or services, (3) Your business information (name, address, entity type), and (4) The date you first used the mark in commerce. Our team will guide you through the entire process step-by-step.
              </div>
            </details>
          </div>
        </div>
      </section>

      {/* Final CTA Banner */}
      <section className="py-20 bg-gradient-to-br from-[#C0392B] via-[#A93226] to-[#8B2E1F] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '40px 40px'}} />
        </div>
        
        <div className="container mx-auto px-4 max-w-[1320px] relative z-10 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Stop Losing Money to Copycats & Brand Thieves!
          </h2>
          <p className="text-xl text-white/90 mb-4 max-w-3xl mx-auto">
            Without a registered trademark, competitors can legally steal your customers by using YOUR brand name. Legal battles cost $100,000+. Trademark protection costs just $45.
          </p>
          <p className="text-2xl font-bold text-white mb-8">
            ⚡ Act Now: File Today & Save Your Business Tomorrow
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <button
              onClick={openPopup}
              className="bg-white text-[#C0392B] hover:bg-gray-100 px-10 py-5 rounded-md font-bold text-xl transition-all duration-300 shadow-2xl hover:shadow-3xl hover:scale-105"
            >
              Register My Trademark
            </button>
            <button
              onClick={handleChatClick}
              className="bg-transparent text-white border-2 border-white hover:bg-white hover:text-[#C0392B] px-10 py-5 rounded-md font-bold text-xl transition-all duration-300"
            >
              Chat Now
            </button>
          </div>
        </div>
      </section>

      <LandingPageFooter />
    </div>
  );
};

export default LandingPage;